
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Col, Row } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import { getHolidaysByYear } from 'festivos-colombia';

// Register Spanish locale for DatePicker
registerLocale('es', es);

const AppointmentModal = ({ show, handleClose }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    correo: '',
    movil: '',
    ciudad: '',
    servicio: '',
    otroServicio: '',
    tipoReunion: 'Virtual'
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [status, setStatus] = useState(null);
  const [showPresencialOption, setShowPresencialOption] = useState(false);

  const allowedCities = ['bucaramanga', 'floridablanca', 'giron', 'piedecuesta'];

  // Effect to check city and show/hide in-person option
  useEffect(() => {
    const city = formData.ciudad.toLowerCase().trim();
    const isAllowed = allowedCities.includes(city);
    setShowPresencialOption(isAllowed);

    // If city is not allowed and 'Presencial' was selected, reset to 'Virtual'
    if (!isAllowed && formData.tipoReunion === 'Presencial') {
      setFormData(prev => ({ ...prev, tipoReunion: 'Virtual' }));
    }
  }, [formData.ciudad, formData.tipoReunion]);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- Date and Time Filtering Logic ---
  const holidays = getHolidaysByYear(new Date().getFullYear());
  const isHoliday = (date) => {
    // Create a date at midnight in the local timezone for comparison
    const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    return holidays.some(holiday => {
      // The library returns dates in YYYY-MM-DD format. Appending time and timezone info
      // ensures the date is parsed correctly in the local timezone.
      const holidayDate = new Date(holiday.date + 'T00:00:00');
      return dateAtMidnight.getTime() === holidayDate.getTime();
    });
  };

  const filterDate = (date) => {
    const day = date.getDay();
    // Disable Sundays (day === 0) and holidays
    return day !== 0 && !isHoliday(date);
  };

  const getTimesForDate = (date) => {
    if (!date) return [];
    const day = date.getDay();
    const times = [];
    const setTime = (hour, minute) => {
        const newDate = new Date(date);
        newDate.setHours(hour, minute, 0, 0);
        return newDate;
    };

    // Monday to Friday (1 to 5)
    if (day >= 1 && day <= 5) {
        // Morning: 9:00, 9:30, 10:00, 10:30, 11:00, 11:30
        for (let i = 9; i <= 11; i++) {
            times.push(setTime(i, 0));
            if (i < 11 || (i === 11 && 0 <= 30)) { // include 11:30
                 times.push(setTime(i, 30));
            }
        }
        // Afternoon: 14:00 to 17:00
        for (let i = 14; i <= 17; i++) {
             times.push(setTime(i, 0));
            if (i < 17) {
                times.push(setTime(i, 30));
            }
        }
    }

    // Saturday (6)
    if (day === 6) {
        // Morning only: 9:00 to 11:30
        for (let i = 9; i <= 11; i++) {
            times.push(setTime(i, 0));
            if (i < 11 || (i === 11 && 0 <= 30)) { // include 11:30
                 times.push(setTime(i, 30));
            }
        }
    }
    
    return times;
  };
  // --- End of Date and Time Logic ---


  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const finalData = {
      ...formData,
      fechaCita: selectedDate ? selectedDate.toISOString() : null
    };

    try {
      const response = await fetch('http://localhost:5001/api/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        // Reset form
        setFormData({ nombre: '', empresa: '', correo: '', movil: '', ciudad: '', servicio: '', otroServicio: '', tipoReunion: 'Virtual' });
        setSelectedDate(null);
      } else {
        setStatus('error');
        console.error('Error del servidor:', data.message);
      }
    } catch (error) {
      setStatus('error');
      console.error('Error de red:', error);
    }
  };

  const services = ["Desarrollo Web", "Marketing Digital", "Consultoría SEO", "Gestión de Redes Sociales", "Diseño Gráfico", "Otro"];

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Agenda tu Asesoría Gratuita</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {status === 'success' && <Alert variant="success">¡Tu cita ha sido agendada con éxito! Te contactaremos pronto.</Alert>}
        {status === 'error' && <Alert variant="danger">Hubo un error al agendar tu cita. Por favor, inténtalo de nuevo.</Alert>}
        
        <Form onSubmit={handleSubmit}>
          {/* Contact Info */}
          <Row>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Nombre</Form.Label><Form.Control type="text" placeholder="Tu nombre" name="nombre" value={formData.nombre} onChange={handleChange} required /></Form.Group></Col>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Correo Electrónico</Form.Label><Form.Control type="email" placeholder="tu@email.com" name="correo" value={formData.correo} onChange={handleChange} required /></Form.Group></Col>
          </Row>
          <Row>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Teléfono Móvil</Form.Label><Form.Control type="tel" placeholder="Tu número de móvil" name="movil" value={formData.movil} onChange={handleChange} required /></Form.Group></Col>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Empresa (Opcional)</Form.Label><Form.Control type="text" placeholder="Nombre de tu empresa" name="empresa" value={formData.empresa} onChange={handleChange} /></Form.Group></Col>
          </Row>
          <hr />

          {/* Service and City */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Servicio de Interés</Form.Label>
                <Form.Select name="servicio" value={formData.servicio} onChange={handleChange} required>
                  <option value="">Selecciona un servicio...</option>
                  {services.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Form.Group>
              {formData.servicio === 'Otro' && (
                <Form.Group className="mb-3"><Form.Label>Especifica el servicio</Form.Label><Form.Control type="text" placeholder="¿Qué tienes en mente?" name="otroServicio" value={formData.otroServicio} onChange={handleChange} required /></Form.Group>
              )}
            </Col>
            <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tu Ciudad</Form.Label>
                  <Form.Control type="text" placeholder="Escribe tu ciudad para ver opciones" name="ciudad" value={formData.ciudad} onChange={handleChange} required />
                </Form.Group>
            </Col>
          </Row>

          {/* Date and Meeting Type */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha y Hora Preferida</Form.Label>
                <DatePicker
                  locale="es"
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  filterDate={filterDate}
                  includeTimes={getTimesForDate(selectedDate)}
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  className="form-control"
                  placeholderText="Elige una fecha y hora"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Reunión</Form.Label>
                <div>
                  <Form.Check inline label="Virtual (Meet)" name="tipoReunion" type="radio" value="Virtual" checked={formData.tipoReunion === 'Virtual'} onChange={handleChange} />
                  {showPresencialOption && (
                    <Form.Check inline label="Presencial" name="tipoReunion" type="radio" value="Presencial" checked={formData.tipoReunion === 'Presencial'} onChange={handleChange} />
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <div className="d-grid mt-4">
            <Button variant="primary" size="lg" type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? 'Agendando...' : 'Confirmar Cita'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AppointmentModal;
